import { Spinner2 } from 'components';
import React from 'react';
import { useTranslation } from 'react-i18next';

const RED = '#FA3F58';
const RED_TINT = 'rgba(250, 63, 88, 0.08)';
const RED_LINE = 'rgba(250, 63, 88, 0.28)';

const priceText = (plan) => (plan?.price ? `${plan.price}/${plan.period}` : '');

const Check = () => (
    <span
        className="mt-0.5 mr-3 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: 'rgba(250, 63, 88, 0.12)' }}
    >
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
            <path d="M2 5.2l2 2 4-4.4" stroke={RED} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    </span>
);

const ChangePlanView = ({ canUpgrade, message, currentPlan, newPlan, features, isProcessing, isMobile, isIntervalChange, onCancel, onConfirm }) => {
    const { t } = useTranslation();

    return (
        <div data-testid="change-plan-view" className={`px-6 pt-4 ${isMobile ? 'pb-32' : 'pb-8'}`}>
            <h1 className="text-[28px] font-bold leading-tight tracking-tight text-[#20252D]">
                {isIntervalChange
                    ? t('subscription.switchToAnnualBilling')
                    : t('subscription.moveToPlan', { plan: newPlan?.name })}
            </h1>
            {message && <p className="mt-2 text-[15px] text-[#5F6F86]">{message}</p>}

            <div className="mt-8">
                {currentPlan && (
                    <div className="grid grid-cols-[24px_1fr] gap-x-3">
                        {/* The rail runs from this dot's centre down through the gap to the next dot. */}
                        <div data-testid="rail-current" className="relative flex justify-center">
                            <span className="absolute bottom-0 top-1/2 w-px bg-[#D2D8DF]" aria-hidden="true" />
                            <span className="relative top-[calc(50%-5px)] h-[10px] w-[10px] rounded-full border-2 border-[#B5BDCA] bg-white" aria-hidden="true" />
                        </div>
                        <div className="flex items-center pb-6">
                            <div className="min-w-0 flex-1">
                                <p className="text-[12px] text-[#7989A0]">{t('subscription.currentPlan') || 'Current'}</p>
                                <p className="text-[16px] font-semibold text-[#5F6F86]">{currentPlan.name}</p>
                            </div>
                            <p className="text-[14px] text-[#7989A0]">{priceText(currentPlan)}</p>
                        </div>
                    </div>
                )}

                <div className={currentPlan ? 'grid grid-cols-[24px_1fr] gap-x-3' : ''}>
                    {currentPlan && (
                        <div data-testid="rail-new" className="relative flex justify-center">
                            <span className="absolute bottom-1/2 top-0 w-px bg-[#D2D8DF]" aria-hidden="true" />
                            <span
                                className="relative top-[calc(50%-5px)] h-[10px] w-[10px] rounded-full"
                                style={{ backgroundColor: RED }}
                                aria-hidden="true"
                            />
                        </div>
                    )}
                    <div
                        className="flex items-center rounded-2xl border px-4 py-4"
                        style={{ backgroundColor: RED_TINT, borderColor: RED_LINE }}
                    >
                        <div className="min-w-0 flex-1">
                            <p className="text-[12px] font-semibold" style={{ color: RED }}>
                                {t('subscription.newPlan') || 'New Plan'}
                            </p>
                            <p className="text-[18px] font-bold text-[#20252D]">{newPlan?.name}</p>
                        </div>
                        <p className="text-[15px] font-bold text-[#20252D]">{priceText(newPlan)}</p>
                    </div>
                </div>
            </div>

            {features?.length > 0 && (
                <div className="mt-9">
                    <h2 className="mb-3 text-[14px] font-bold text-[#20252D]">
                        {t('paymentMethod.whatsIncluded') || "What's included:"}
                    </h2>
                    <ul>
                        {features.map((feature, i) => (
                            <li key={i} className="mb-2.5 flex items-start">
                                <Check />
                                <span className="text-[14px] leading-snug text-[#353E4A]">{feature}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {canUpgrade && (
                <div className="mt-7 border-l-2 py-0.5 pl-4" style={{ borderColor: RED }}>
                    <p className="text-[14px] font-semibold text-[#20252D]">
                        {t('subscription.prorationNotice') || 'Prorated Billing Applied'}
                    </p>
                    <p className="mt-0.5 text-[13px] text-[#5F6F86]">
                        {t('subscription.prorationExplanation') || "You'll be charged a prorated amount for the upgrade."}
                    </p>
                </div>
            )}

            <div className="mt-10 flex items-center gap-3">
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={isProcessing}
                    className="h-12 flex-1 rounded-full text-[15px] font-bold text-[#20252D] transition-colors hover:bg-[#F0F2F4] disabled:opacity-50"
                >
                    {t('common.cancel') || 'Cancel'}
                </button>
                <button
                    type="button"
                    onClick={onConfirm}
                    disabled={isProcessing}
                    className="flex h-12 flex-[1.4] items-center justify-center rounded-full bg-[#20252D] text-[15px] font-bold text-white transition-colors hover:bg-black disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {isProcessing ? <Spinner2 col="#ffffff" /> : t('subscription.confirmUpgrade') || 'Confirm Change'}
                </button>
            </div>
        </div>
    );
};

export default ChangePlanView;
